import { useState, useRef, useCallback, useMemo } from 'react'
import { Pencil, ChevronDown, ChevronUp } from 'lucide-react'
import type { AppEntry } from '../store/appsSlice'
import { updateApp } from '../store/appsSlice'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { isEmailAppEditor } from '../store/editorsSlice'
import './AppHeader.css'

interface AppHeaderProps {
  app: AppEntry
}

const statusDisplayMap: Record<string, string> = {
  building: 'STATUS: Building',
  finished: 'STATUS: Completed (Successfully Deployed)',
  queued: 'STATUS: Queued',
  hanged: 'STATUS: Hanged (No Activity)',
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffSec = Math.floor(diffMs / 1000)
  if (diffSec < 60) return 'just now'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin} min ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDays = Math.floor(diffHr / 24)
  return `${diffDays}d ago`
}

function extractHostname(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

function AppHeader({ app }: AppHeaderProps) {
  const dispatch = useAppDispatch()
  const authUser = useAppSelector((state) => state.auth.user)
  const editors = useAppSelector((state) => state.editors.items)

  const isEditor = !!authUser && isEmailAppEditor(authUser.email, editors)

  const [editingName, setEditingName] = useState(false)
  const [editingDesc, setEditingDesc] = useState(false)
  const [descExpanded, setDescExpanded] = useState(false)
  const [nameValue, setNameValue] = useState(app.name)
  const [descValue, setDescValue] = useState(app.description)
  const cancellingNameRef = useRef(false)
  const cancellingDescRef = useRef(false)
  const savingNameRef = useRef(false)
  const savingDescRef = useRef(false)

  const saveName = useCallback(async () => {
    if (cancellingNameRef.current || savingNameRef.current) return
    const trimmed = nameValue.trim()
    if (!trimmed || trimmed === app.name) {
      setNameValue(app.name)
      setEditingName(false)
      return
    }
    savingNameRef.current = true
    await dispatch(updateApp({ appId: app.id, name: trimmed }))
    savingNameRef.current = false
    setEditingName(false)
  }, [dispatch, app.id, app.name, nameValue])

  const saveDescription = useCallback(async () => {
    if (cancellingDescRef.current || savingDescRef.current) return
    if (descValue === app.description) {
      setEditingDesc(false)
      return
    }
    savingDescRef.current = true
    await dispatch(updateApp({ appId: app.id, description: descValue }))
    savingDescRef.current = false
    setEditingDesc(false)
  }, [dispatch, app.id, app.description, descValue])

  const DESC_LINE_LIMIT = 20
  const descLines = useMemo(() => app.description.split('\n'), [app.description])
  const descNeedsTruncation = descLines.length > DESC_LINE_LIMIT
  const displayedDescription = descExpanded || !descNeedsTruncation
    ? app.description
    : descLines.slice(0, DESC_LINE_LIMIT).join('\n')

  const statusText = statusDisplayMap[app.status] || app.status
  const metaParts: string[] = []
  if (app.created_at) metaParts.push(`Created: ${formatDate(app.created_at)}`)
  if (app.model) metaParts.push(`Model: ${app.model}`)
  if (app.deployment_url) metaParts.push(`Deployment: ${extractHostname(app.deployment_url)}`)
  if (app.status === 'building' && app.container_name) metaParts.push(`Container: ${app.container_name}`)

  return (
    <header className="app-header" data-testid="app-header">
      <div className="app-header__title-row">
        {editingName ? (
          <input
            className="app-header__title-input"
            data-testid="app-header-title-input"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                saveName()
              } else if (e.key === 'Escape') {
                cancellingNameRef.current = true
                setNameValue(app.name)
                setEditingName(false)
                setTimeout(() => { cancellingNameRef.current = false }, 0)
              }
            }}
            autoFocus
          />
        ) : (
          <h1
            className={`app-header__title${isEditor ? ' app-header__title--editable' : ''}`}
            data-testid="app-header-title"
            onClick={isEditor ? () => {
              setNameValue(app.name)
              setEditingName(true)
            } : undefined}
          >
            {app.name}
            {isEditor && <Pencil size={14} className="app-header__edit-icon" aria-hidden="true" />}
          </h1>
        )}
        <span
          className={`app-header__status app-header__status--${app.status}`}
          data-testid="app-header-status"
        >
          {statusText}
        </span>
      </div>
      {editingDesc ? (
        <textarea
          className="app-header__description-input"
          data-testid="app-header-description-input"
          value={descValue}
          onChange={(e) => setDescValue(e.target.value)}
          onBlur={saveDescription}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              cancellingDescRef.current = true
              setDescValue(app.description)
              setEditingDesc(false)
              setTimeout(() => { cancellingDescRef.current = false }, 0)
            }
          }}
          autoFocus
          rows={3}
        />
      ) : (
        <div className="app-header__description-wrapper">
          <p
            className={`app-header__description${isEditor ? ' app-header__description--editable' : ''}`}
            data-testid="app-header-description"
            onClick={isEditor ? () => {
              setDescValue(app.description)
              setEditingDesc(true)
            } : undefined}
          >
            {displayedDescription}
            {isEditor && <Pencil size={12} className="app-header__edit-icon" aria-hidden="true" />}
          </p>
          {descNeedsTruncation && (
            <button
              className="app-header__expand-btn"
              data-testid="app-header-description-expand"
              onClick={() => setDescExpanded((prev) => !prev)}
              aria-expanded={descExpanded}
            >
              {descExpanded ? (
                <>Show less <ChevronUp size={14} /></>
              ) : (
                <>Show more <ChevronDown size={14} /></>
              )}
            </button>
          )}
        </div>
      )}
      {metaParts.length > 0 && (
        <div className="app-header__meta" data-testid="app-header-meta">
          {metaParts.map((part, i) => (
            <span key={i}>
              {i > 0 && <span className="app-header__meta-sep"> | </span>}
              {part}
            </span>
          ))}
        </div>
      )}
      <div className="app-header__timestamps" data-testid="app-header-timestamps">
        <span className="app-header__timestamp" data-testid="app-header-queued-at">
          Queued {formatRelativeTime(app.created_at)}
        </span>
        {app.started_at && (
          <span className="app-header__timestamp" data-testid="app-header-started-at">
            Started {formatRelativeTime(app.started_at)}
          </span>
        )}
        {app.finished_at && (
          <span className="app-header__timestamp" data-testid="app-header-finished-at">
            Finished {formatRelativeTime(app.finished_at)}
          </span>
        )}
      </div>
    </header>
  )
}

export default AppHeader
